import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Stack, Divider,
  InputAdornment, CircularProgress,
} from '@mui/material';
import {
  LockOpen, LockOutlined, Close, Storefront, Monitor, CalendarToday,
  CheckCircle, Warning, InfoOutlined,
} from '@mui/icons-material';
import { useGetDenominationsQuery } from '../../services/denominationApi';
import {
  useOpenSessionMutation, useCloseSessionMutation,
  useLazyListSessionsQuery, useLazyGetSessionQuery,
} from '../../services/cashSessionApi';
import { useToast } from '../common/ToastProvider';
import DenominationCountGrid, { fmt } from './DenominationCountGrid';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#F8FAFC',
    borderRadius: '8px',
    fontSize: '0.875rem',
    '& fieldset': { borderColor: '#E2E8F0' },
    '&:hover fieldset': { borderColor: '#CBD5E1' },
    '&.Mui-focused fieldset': { borderColor: '#4F46E5' },
  },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4F46E5' },
};

const SummaryRow = ({ label, value, bold, color, negate }) => {
  const num = parseFloat(value || 0);
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
      <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.73rem' }, color: color || '#64748B', fontWeight: bold ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.73rem' }, color: color || '#1E293B', fontWeight: bold ? 700 : 500 }}>
        {negate ? fmt(-num) : fmt(num)}
      </Typography>
    </Box>
  );
};

const StepDots = ({ step, total = 2 }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    {Array.from({ length: total }).map((_, i) => (
      <Box
        key={i}
        sx={{
          width: 6, height: 6, borderRadius: '50%',
          bgcolor: i === step ? '#4F46E5' : '#E2E8F0',
        }}
      />
    ))}
  </Stack>
);

const StepLabel = ({ step, color }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: color || '#4F46E5' }}>
      Step {step + 1} of 2
    </Typography>
    <StepDots step={step} />
  </Stack>
);

/**
 * Self-contained replacement for OpenSessionModal / CloseSessionModal, used only when
 * organization.require_denomination_counting === true. mode="open" | "close".
 * When denominations are available, presents a 2-step flow (Count → Review/Confirm)
 * so the dialog stays within a compact maxWidth="xs" footprint on every screen size.
 */
const CashDenominationSessionModal = ({
  mode, open,
  store, terminal, onSessionOpened,
  onClose, session, onSessionClosed,
}) => {
  const isOpenMode = mode === 'open';
  const showToast = useToast();

  const { data: denominations = [], isLoading: isLoadingDenoms, isError: isDenomError } =
    useGetDenominationsQuery({ currency_code: 'INR' }, { skip: !open });

  const [step, setStep]                     = useState(0);
  const [counts, setCounts]                 = useState({});
  const [notes, setNotes]                   = useState('');
  const [manualBalance, setManualBalance]   = useState('');
  const [balanceError, setBalanceError]     = useState('');
  const [liveSummary, setLiveSummary]       = useState(null);

  const [openSession,         { isLoading: isOpening }]  = useOpenSessionMutation();
  const [triggerListSessions]                            = useLazyListSessionsQuery();
  const [closeSession,        { isLoading: isClosing }]  = useCloseSessionMutation();
  const [triggerGetSession,   { isLoading: isFetchingSession }] = useLazyGetSessionQuery();

  const isSubmitting = isOpenMode ? isOpening : isClosing;

  // Reset / fetch on open
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setCounts({});
    setNotes('');
    setManualBalance('');
    setBalanceError('');
    setLiveSummary(null);

    if (!isOpenMode && session?.id) {
      triggerGetSession(session.id)
        .unwrap()
        .then((res) => { if (res?.data?.session) setLiveSummary(res.data.session); })
        .catch(() => showToast('Could not load session summary. Please try again.', 'error'));
    }
  }, [open]); // eslint-disable-line

  const useFallback = !isLoadingDenoms && (isDenomError || denominations.length === 0);

  const gridTotal = useMemo(
    () => denominations.reduce((sum, d) => sum + (d.value || 0) * (counts[d.id] || 0), 0),
    [denominations, counts]
  );

  const handleCountChange = (id, delta) => {
    setCounts((prev) => {
      const next = Math.max(0, (prev[id] || 0) + delta);
      return { ...prev, [id]: next };
    });
  };

  const handleManualBalanceChange = (e) => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(val)) {
      setManualBalance(val);
      if (balanceError) setBalanceError('');
    }
  };

  const displaySession = liveSummary || session;
  const summary        = displaySession?.summary || {};

  const expectedCash = useMemo(() => {
    const val = parseFloat(displaySession?.summary?.expected_cash ?? displaySession?.opening_balance ?? 0);
    return isNaN(val) ? 0 : val;
  }, [displaySession]);

  const currentClosingBalance = useFallback ? parseFloat(manualBalance) : gridTotal;

  const diffResult = useMemo(() => {
    if (isOpenMode) return null;
    if (useFallback && (manualBalance === '' || isNaN(currentClosingBalance))) return null;
    const diff = currentClosingBalance - expectedCash;
    if (diff > 0) return { label: `OVER BY ${fmt(diff)}`,   icon: 'check', color: '#065F46', bg: '#D1FAE5' };
    if (diff < 0) return { label: `SHORT BY ${fmt(-diff)}`, icon: 'warn',  color: '#7F1D1D', bg: '#FEE2E2' };
    return            { label: 'BALANCED',                   icon: 'check', color: '#065F46', bg: '#D1FAE5' };
  }, [isOpenMode, useFallback, manualBalance, currentClosingBalance, expectedCash]);

  const validateFallback = () => {
    const val = parseFloat(manualBalance);
    if (manualBalance === '' || isNaN(val)) {
      setBalanceError(isOpenMode ? 'Opening balance is required' : 'Closing balance is required');
      return false;
    }
    if (val < 0) {
      setBalanceError(isOpenMode ? 'Opening balance cannot be negative' : 'Closing balance cannot be negative');
      return false;
    }
    setBalanceError('');
    return true;
  };

  const handleSubmit = async () => {
    if (useFallback && !validateFallback()) return;

    const balance = useFallback ? parseFloat(manualBalance) : gridTotal;
    const denominationEntries = useFallback
      ? undefined
      : denominations
          .filter((d) => (counts[d.id] || 0) > 0)
          .map((d) => ({ denomination_id: d.id, count: counts[d.id] }));

    if (isOpenMode) {
      try {
        const res = await openSession({
          opening_balance: balance,
          notes: notes.trim() || undefined,
          denomination_entries: denominationEntries,
        }).unwrap();
        onSessionOpened(res.data.session);
      } catch (err) {
        if (err?.status === 422) {
          try {
            const listRes = await triggerListSessions({ per_page: 10 }).unwrap();
            const sessions = listRes?.data?.sessions || [];
            const existing = sessions.find((s) => s.status === 'open');
            if (existing) {
              showToast('Session already open — resuming existing session', 'info');
              onSessionOpened(existing);
              return;
            }
          } catch { /* fall through */ }
        }
        showToast(err?.data?.error || 'Failed to open session. Please try again.', 'error');
      }
    } else {
      try {
        const res = await closeSession({
          id: session.id,
          closing_balance: balance,
          denomination_entries: denominationEntries,
        }).unwrap();
        onSessionClosed(res.data.session);
        showToast('Session closed successfully', 'success');
      } catch (err) {
        showToast(err?.data?.error || 'Failed to close session. Please try again.', 'error');
      }
    }
  };

  const dateStr = new Date().toLocaleDateString('en-IN', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });

  const renderGrid = (totalLabel) => {
    if (isLoadingDenoms) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} sx={{ color: '#4F46E5' }} />
        </Box>
      );
    }
    return (
      <DenominationCountGrid
        denominations={denominations}
        counts={counts}
        onChange={handleCountChange}
        totalLabel={totalLabel}
      />
    );
  };

  const dialogPaperSx = {
    borderRadius: { xs: '12px', sm: '14px' },
    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
    m: { xs: 2, sm: 4 },
    width: { xs: 'calc(100% - 32px)', sm: 'auto' },
    maxWidth: { xs: 'calc(100% - 32px)', sm: '480px' },
  };

  // ============================================================
  // OPEN MODE
  // ============================================================
  if (isOpenMode) {

    // Fallback: denominations unavailable — single compact screen, manual entry.
    if (useFallback) {
      return (
        <Dialog
          open={open}
          disableEscapeKeyDown
          onClose={(_, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          }}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.75, sm: 2.5 }, pb: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{
              width: { xs: 30, sm: 36 }, height: { xs: 30, sm: 36 },
              borderRadius: '9px', bgcolor: '#EEF2FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <LockOpen sx={{ fontSize: { xs: 17, sm: 20 }, color: '#4F46E5' }} />
            </Box>
            <Box>
              <Typography fontWeight={800} fontSize={{ xs: '0.92rem', sm: '1rem' }} color="#1E293B" lineHeight={1.3}>
                Open Cash Session
              </Typography>
              <Typography fontSize={{ xs: '0.68rem', sm: '0.72rem' }} color="#94A3B8" fontWeight={500}>
                Count the opening cash drawer
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.5, sm: 2 }, pb: 0 }}>
            <Box sx={{
              bgcolor: '#F8FAFC', border: '1px solid #F1F5F9',
              borderRadius: '8px',
              px: { xs: 1.25, sm: 1.5 }, py: { xs: 0.75, sm: 1 },
              mb: { xs: 1.5, sm: 2 },
            }}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Storefront sx={{ fontSize: 12, color: '#64748B' }} />
                  <Typography fontSize={{ xs: '0.72rem', sm: '0.75rem' }} fontWeight={600} color="#1E293B" noWrap>
                    {store?.name || 'Store'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Monitor sx={{ fontSize: 12, color: '#64748B' }} />
                  <Typography fontSize={{ xs: '0.72rem', sm: '0.75rem' }} fontWeight={600} color="#1E293B" noWrap>
                    {terminal?.terminal_number ? `${terminal.terminal_number} · ${terminal.name}` : terminal?.name || 'Terminal'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <CalendarToday sx={{ fontSize: 12, color: '#64748B' }} />
                  <Typography fontSize={{ xs: '0.68rem', sm: '0.72rem' }} color="#64748B" noWrap>
                    {dateStr}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1,
              bgcolor: '#FFFBEB', border: '1px solid #FEF3C7',
              borderRadius: '8px', px: { xs: 1.25, sm: 1.5 }, py: { xs: 0.6, sm: 1 }, mb: 1.5,
            }}>
              <InfoOutlined sx={{ fontSize: 14, color: '#92400E', mt: 0.15, flexShrink: 0 }} />
              <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.72rem' }, color: '#92400E', lineHeight: 1.5 }}>
                Denomination list unavailable — enter the total amount manually.
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Opening Cash Balance"
              required
              value={manualBalance}
              onChange={handleManualBalanceChange}
              disabled={isSubmitting}
              error={!!balanceError}
              helperText={balanceError || ' '}
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 700, color: '#64748B', fontSize: '0.9rem' }}>₹</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{ ...fieldSx, mb: 0.5 }}
            />

            <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

            <TextField
              fullWidth
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g. Morning shift"
              sx={{ ...fieldSx, mb: 0.5 }}
            />
          </DialogContent>

          <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.25, sm: 2 }, pb: { xs: 1.75, sm: 2.5 } }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || manualBalance === ''}
              disableElevation
              startIcon={!isSubmitting ? <LockOpen sx={{ fontSize: '17px !important' }} /> : null}
              sx={{
                bgcolor: '#4F46E5', color: '#fff',
                py: { xs: 1, sm: 1.3 },
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                fontWeight: 700,
                borderRadius: '8px', textTransform: 'none',
                '&:hover': { bgcolor: '#4338CA' },
                '&.Mui-disabled': { bgcolor: '#C7D2FE', color: '#fff' },
              }}
            >
              {isSubmitting ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={15} sx={{ color: '#fff' }} />
                  <span>Starting Session...</span>
                </Stack>
              ) : (
                'Start Session'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    // Step 1 of 2: Count Drawer
    if (step === 0) {
      return (
        <Dialog
          open={open}
          disableEscapeKeyDown
          onClose={(_, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          }}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.75, sm: 2.5 }, pb: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{
              width: { xs: 30, sm: 36 }, height: { xs: 30, sm: 36 },
              borderRadius: '9px', bgcolor: '#EEF2FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <LockOpen sx={{ fontSize: { xs: 17, sm: 20 }, color: '#4F46E5' }} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography fontWeight={800} fontSize={{ xs: '0.92rem', sm: '1rem' }} color="#1E293B" lineHeight={1.3}>
                Open Cash Session
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                <Typography fontSize={{ xs: '0.68rem', sm: '0.72rem' }} color="#94A3B8" fontWeight={500}>
                  Count the opening cash drawer
                </Typography>
              </Stack>
            </Box>
            <StepLabel step={0} />
          </DialogTitle>

          <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.5, sm: 2 }, pb: 0 }}>
            {renderGrid('Opening Balance')}
          </DialogContent>

          <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.25, sm: 2 }, pb: { xs: 1.75, sm: 2.5 } }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setStep(1)}
              disabled={isLoadingDenoms}
              disableElevation
              sx={{
                bgcolor: '#4F46E5', color: '#fff',
                py: { xs: 1, sm: 1.3 },
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                fontWeight: 700,
                borderRadius: '8px', textTransform: 'none',
                '&:hover': { bgcolor: '#4338CA' },
                '&.Mui-disabled': { bgcolor: '#C7D2FE', color: '#fff' },
              }}
            >
              Next
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    // Step 2 of 2: Review & Start
    return (
      <Dialog
        open={open}
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.75, sm: 2.5 }, pb: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            width: { xs: 30, sm: 36 }, height: { xs: 30, sm: 36 },
            borderRadius: '9px', bgcolor: '#EEF2FF',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <LockOpen sx={{ fontSize: { xs: 17, sm: 20 }, color: '#4F46E5' }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography fontWeight={800} fontSize={{ xs: '0.92rem', sm: '1rem' }} color="#1E293B" lineHeight={1.3}>
              Open Cash Session
            </Typography>
            <Typography fontSize={{ xs: '0.68rem', sm: '0.72rem' }} color="#94A3B8" fontWeight={500}>
              Review and start your session
            </Typography>
          </Box>
          <StepLabel step={1} />
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.5, sm: 2 }, pb: 0 }}>
          <Box sx={{
            bgcolor: '#F8FAFC', border: '1px solid #F1F5F9',
            borderRadius: '8px',
            px: { xs: 1.25, sm: 1.5 }, py: { xs: 0.75, sm: 1 },
            mb: { xs: 1.5, sm: 2 },
          }}>
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Storefront sx={{ fontSize: 12, color: '#64748B' }} />
                <Typography fontSize={{ xs: '0.72rem', sm: '0.75rem' }} fontWeight={600} color="#1E293B" noWrap>
                  {store?.name || 'Store'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Monitor sx={{ fontSize: 12, color: '#64748B' }} />
                <Typography fontSize={{ xs: '0.72rem', sm: '0.75rem' }} fontWeight={600} color="#1E293B" noWrap>
                  {terminal?.terminal_number ? `${terminal.terminal_number} · ${terminal.name}` : terminal?.name || 'Terminal'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CalendarToday sx={{ fontSize: 12, color: '#64748B' }} />
                <Typography fontSize={{ xs: '0.68rem', sm: '0.72rem' }} color="#64748B" noWrap>
                  {dateStr}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            bgcolor: '#F0FDF4', border: '1px solid #BBF7D0',
            borderRadius: '8px',
            px: { xs: 1.25, sm: 1.5 }, py: { xs: 0.75, sm: 1 },
            mb: { xs: 1.5, sm: 2 },
          }}>
            <Typography fontSize={{ xs: '0.78rem', sm: '0.85rem' }} fontWeight={700} color="#065F46">
              Opening Balance
            </Typography>
            <Typography fontSize={{ xs: '1rem', sm: '1.1rem' }} fontWeight={800} color="#065F46">
              {fmt(gridTotal)}
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
            placeholder="e.g. Morning shift"
            sx={{ ...fieldSx, mb: 0.5 }}
          />
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.25, sm: 2 }, pb: { xs: 1.75, sm: 2.5 }, gap: 1 }}>
          <Button
            variant="outlined" onClick={() => setStep(0)} disabled={isSubmitting}
            sx={{
              flex: 1, py: { xs: 0.9, sm: 1.1 }, fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              borderRadius: '8px', textTransform: 'none',
              borderColor: '#E2E8F0', color: '#64748B',
              '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained" onClick={handleSubmit}
            disabled={isSubmitting}
            disableElevation
            startIcon={!isSubmitting ? <LockOpen sx={{ fontSize: '17px !important' }} /> : null}
            sx={{
              flex: 2, py: { xs: 0.9, sm: 1.1 }, fontWeight: 700,
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              borderRadius: '8px', textTransform: 'none',
              bgcolor: '#4F46E5', color: '#fff',
              '&:hover': { bgcolor: '#4338CA' },
              '&.Mui-disabled': { bgcolor: '#C7D2FE', color: '#fff' },
            }}
          >
            {isSubmitting ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={15} sx={{ color: '#fff' }} />
                <span>Starting...</span>
              </Stack>
            ) : (
              'Start Session'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ============================================================
  // CLOSE MODE
  // ============================================================
  const closeDialogPaperSx = {
    borderRadius: { xs: '12px', sm: '12px' },
    m: { xs: 2, sm: 4 },
    width: { xs: 'calc(100% - 32px)', sm: 'auto' },
    maxWidth: { xs: 'calc(100% - 32px)', sm: '480px' },
  };

  const closeTitleHeader = (showStepLabel, stepIndex) => (
    <DialogTitle sx={{
      px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 2 },
      display: 'flex', alignItems: 'center', gap: 1.25,
      borderBottom: '1px solid #F1F5F9',
    }}>
      <Box sx={{
        width: { xs: 28, sm: 34 }, height: { xs: 28, sm: 34 },
        borderRadius: '8px', bgcolor: '#FEF2F2',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <LockOutlined sx={{ fontSize: { xs: 16, sm: 19 }, color: '#EF4444' }} />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography fontWeight={800} fontSize={{ xs: '0.9rem', sm: '1rem' }} color="#1E293B" lineHeight={1.3}>
          Close Cash Session
        </Typography>
        {displaySession?.session_number && (
          <Typography fontSize={{ xs: '0.65rem', sm: '0.7rem' }} color="#94A3B8" fontWeight={500} noWrap>
            {displaySession.session_number}
          </Typography>
        )}
      </Box>
      {showStepLabel && <StepLabel step={stepIndex} />}
      <Button
        size="small" onClick={onClose} disabled={isSubmitting}
        sx={{ minWidth: 0, p: 0.5, color: '#94A3B8', flexShrink: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
      >
        <Close sx={{ fontSize: 18 }} />
      </Button>
    </DialogTitle>
  );

  // Fallback: denominations unavailable — single compact screen, manual entry.
  if (useFallback) {
    return (
      <Dialog
        open={open}
        onClose={isSubmitting ? undefined : onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: closeDialogPaperSx }}
      >
        {closeTitleHeader(false)}

        <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.5, sm: 2 }, pb: 1 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05em', mb: 0.75 }}>
            SESSION SUMMARY
          </Typography>

          {isFetchingSession ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} sx={{ color: '#4F46E5' }} />
            </Box>
          ) : (
            <Box sx={{
              bgcolor: '#F8FAFC', border: '1px solid #F1F5F9',
              borderRadius: '8px',
              px: { xs: 1.25, sm: 1.75 }, py: { xs: 0.6, sm: 1 },
              mb: { xs: 1.25, sm: 1.75 },
            }}>
              <SummaryRow label="Opening Balance"  value={displaySession?.opening_balance} />
              <SummaryRow label="Total Cash Sales" value={summary.total_cash_sales} />
              <SummaryRow label="Change Given"     value={summary.total_change_given}  negate />
              <SummaryRow label="Cash Refunds"     value={summary.total_cash_refunds}  negate />
              {parseFloat(summary.total_voucher_sales_cash || 0) > 0 && <SummaryRow label="Voucher Sales (Cash)" value={summary.total_voucher_sales_cash} />}
              {parseFloat(summary.total_voucher_sale_refunds_cash || 0) > 0 && <SummaryRow label="Voucher Refunds (Cash)" value={summary.total_voucher_sale_refunds_cash} negate />}
              <SummaryRow label="Cash Ins"         value={summary.total_cash_ins} />
              <SummaryRow label="Cash Outs"        value={summary.total_cash_outs}     negate />
              <Divider sx={{ my: 0.5 }} />
              <SummaryRow
                label="Expected Cash"
                value={summary.expected_cash ?? displaySession?.opening_balance}
                bold color="#1E293B"
              />
            </Box>
          )}

          <Divider sx={{ mb: { xs: 1.25, sm: 1.75 } }} />

          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1,
            bgcolor: '#FFFBEB', border: '1px solid #FEF3C7',
            borderRadius: '8px', px: { xs: 1.25, sm: 1.5 }, py: { xs: 0.6, sm: 1 }, mb: 1.5,
          }}>
            <InfoOutlined sx={{ fontSize: 14, color: '#92400E', mt: 0.15, flexShrink: 0 }} />
            <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.72rem' }, color: '#92400E', lineHeight: 1.5 }}>
              Denomination list unavailable — enter the total amount manually.
            </Typography>
          </Box>
          <TextField
            fullWidth
            label="Physical Cash Count"
            required
            value={manualBalance}
            onChange={handleManualBalanceChange}
            disabled={isSubmitting}
            error={!!balanceError}
            helperText={balanceError || ' '}
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 700, color: '#64748B', fontSize: '0.9rem' }}>₹</Typography>
                </InputAdornment>
              ),
            }}
            sx={{ ...fieldSx, mb: 0.5 }}
          />

          {diffResult && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: diffResult.bg, borderRadius: '8px',
              px: 1.5, py: { xs: 0.6, sm: 1 }, mt: 1.5, mb: 1,
            }}>
              {diffResult.icon === 'check'
                ? <CheckCircle sx={{ fontSize: 16, color: diffResult.color }} />
                : <Warning      sx={{ fontSize: 16, color: diffResult.color }} />}
              <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 800, color: diffResult.color }}>
                {diffResult.label}
              </Typography>
            </Box>
          )}

          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1,
            bgcolor: '#FFFBEB', border: '1px solid #FEF3C7',
            borderRadius: '8px',
            px: { xs: 1.25, sm: 1.5 }, py: { xs: 0.6, sm: 1 },
            mt: diffResult ? 0 : 1.5, mb: 0.5,
          }}>
            <InfoOutlined sx={{ fontSize: 14, color: '#92400E', mt: 0.15, flexShrink: 0 }} />
            <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.72rem' }, color: '#92400E', lineHeight: 1.5 }}>
              Count carefully. Once confirmed, corrections can only be made by your store manager.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1, sm: 1.5 }, pb: { xs: 1.5, sm: 2.5 }, gap: 1 }}>
          <Button
            variant="outlined" onClick={onClose} disabled={isSubmitting}
            sx={{
              flex: 1, py: { xs: 0.9, sm: 1.1 }, fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              borderRadius: '8px', textTransform: 'none',
              borderColor: '#E2E8F0', color: '#64748B',
              '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained" onClick={handleSubmit}
            disabled={isSubmitting || isFetchingSession || (manualBalance === '')}
            disableElevation
            sx={{
              flex: 2, py: { xs: 0.9, sm: 1.1 }, fontWeight: 700,
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              borderRadius: '8px', textTransform: 'none',
              bgcolor: '#EF4444', color: '#fff',
              '&:hover': { bgcolor: '#DC2626' },
              '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
            }}
          >
            {isSubmitting ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={14} sx={{ color: '#fff' }} />
                <span>Closing...</span>
              </Stack>
            ) : (
              'Confirm & Close'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Step 1 of 2: Session Summary
  if (step === 0) {
    return (
      <Dialog
        open={open}
        onClose={isSubmitting ? undefined : onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: closeDialogPaperSx }}
      >
        {closeTitleHeader(true, 0)}

        <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.5, sm: 2 }, pb: 1 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05em', mb: 0.75 }}>
            SESSION SUMMARY
          </Typography>

          {isFetchingSession ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} sx={{ color: '#4F46E5' }} />
            </Box>
          ) : (
            <Box sx={{
              bgcolor: '#F8FAFC', border: '1px solid #F1F5F9',
              borderRadius: '8px',
              px: { xs: 1.25, sm: 1.75 }, py: { xs: 0.6, sm: 1 },
              mb: { xs: 1.25, sm: 1.75 },
            }}>
              <SummaryRow label="Opening Balance"  value={displaySession?.opening_balance} />
              <SummaryRow label="Total Cash Sales" value={summary.total_cash_sales} />
              <SummaryRow label="Change Given"     value={summary.total_change_given}  negate />
              <SummaryRow label="Cash Refunds"     value={summary.total_cash_refunds}  negate />
              {parseFloat(summary.total_voucher_sales_cash || 0) > 0 && <SummaryRow label="Voucher Sales (Cash)" value={summary.total_voucher_sales_cash} />}
              {parseFloat(summary.total_voucher_sale_refunds_cash || 0) > 0 && <SummaryRow label="Voucher Refunds (Cash)" value={summary.total_voucher_sale_refunds_cash} negate />}
              <SummaryRow label="Cash Ins"         value={summary.total_cash_ins} />
              <SummaryRow label="Cash Outs"        value={summary.total_cash_outs}     negate />
              <Divider sx={{ my: 0.5 }} />
              <SummaryRow
                label="Expected Cash"
                value={summary.expected_cash ?? displaySession?.opening_balance}
                bold color="#1E293B"
              />
            </Box>
          )}

          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1,
            bgcolor: '#FFFBEB', border: '1px solid #FEF3C7',
            borderRadius: '8px', px: { xs: 1.25, sm: 1.5 }, py: { xs: 0.6, sm: 1 },
          }}>
            <InfoOutlined sx={{ fontSize: 14, color: '#92400E', mt: 0.15, flexShrink: 0 }} />
            <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.72rem' }, color: '#92400E', lineHeight: 1.5 }}>
              Next, count the cash drawer note-by-note.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1, sm: 1.5 }, pb: { xs: 1.5, sm: 2.5 }, gap: 1 }}>
          <Button
            variant="outlined" onClick={onClose} disabled={isSubmitting}
            sx={{
              flex: 1, py: { xs: 0.9, sm: 1.1 }, fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              borderRadius: '8px', textTransform: 'none',
              borderColor: '#E2E8F0', color: '#64748B',
              '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained" onClick={() => setStep(1)}
            disabled={isFetchingSession || isLoadingDenoms}
            disableElevation
            sx={{
              flex: 2, py: { xs: 0.9, sm: 1.1 }, fontWeight: 700,
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              borderRadius: '8px', textTransform: 'none',
              bgcolor: '#4F46E5', color: '#fff',
              '&:hover': { bgcolor: '#4338CA' },
              '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
            }}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Step 2 of 2: Count Cash
  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: closeDialogPaperSx }}
    >
      {closeTitleHeader(true, 1)}

      <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.5, sm: 2 }, pb: 1 }}>
        {renderGrid('Closing Balance')}

        {diffResult && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: diffResult.bg, borderRadius: '8px',
            px: 1.5, py: { xs: 0.6, sm: 1 }, mt: 1.5, mb: 1,
          }}>
            {diffResult.icon === 'check'
              ? <CheckCircle sx={{ fontSize: 16, color: diffResult.color }} />
              : <Warning      sx={{ fontSize: 16, color: diffResult.color }} />}
            <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 800, color: diffResult.color }}>
              {diffResult.label}
            </Typography>
          </Box>
        )}

        <Box sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1,
          bgcolor: '#FFFBEB', border: '1px solid #FEF3C7',
          borderRadius: '8px',
          px: { xs: 1.25, sm: 1.5 }, py: { xs: 0.6, sm: 1 },
          mt: diffResult ? 0 : 1.5, mb: 0.5,
        }}>
          <InfoOutlined sx={{ fontSize: 14, color: '#92400E', mt: 0.15, flexShrink: 0 }} />
          <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.72rem' }, color: '#92400E', lineHeight: 1.5 }}>
            Count carefully. Once confirmed, corrections can only be made by your store manager.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1, sm: 1.5 }, pb: { xs: 1.5, sm: 2.5 }, gap: 1 }}>
        <Button
          variant="outlined" onClick={() => setStep(0)} disabled={isSubmitting}
          sx={{
            flex: 1, py: { xs: 0.9, sm: 1.1 }, fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            borderRadius: '8px', textTransform: 'none',
            borderColor: '#E2E8F0', color: '#64748B',
            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained" onClick={handleSubmit}
          disabled={isSubmitting || isLoadingDenoms}
          disableElevation
          sx={{
            flex: 2, py: { xs: 0.9, sm: 1.1 }, fontWeight: 700,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            borderRadius: '8px', textTransform: 'none',
            bgcolor: '#EF4444', color: '#fff',
            '&:hover': { bgcolor: '#DC2626' },
            '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
          }}
        >
          {isSubmitting ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} sx={{ color: '#fff' }} />
              <span>Closing...</span>
            </Stack>
          ) : (
            'Confirm & Close'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashDenominationSessionModal;
